import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { DiffPropagazione, DiffSede, ModificaCampo } from '../../../_helpers/anagrafica-propagazione';
import { AnteprimaImpattoOrdini, PropagaAiOrdiniRequest } from '../../../_services/registry.service';

// Feature 120 (issue #3368) — dialogo di propagazione, mostrato DOPO il salvataggio dell'anagrafica.
// Annullarlo annulla la sola propagazione, non il salvataggio (FR-003, FR-013).
//
// standalone: false, dichiarato in SharedModule accanto ad AnagraficaCreateComponent che lo apre.
@Component({
  selector: 'app-propaga-ordini-modal',
  templateUrl: './propaga-ordini-modal.component.html',
  standalone: false,
})
export class PropagaOrdiniModalComponent implements OnInit {
  @Input() diff: DiffPropagazione;
  @Input() anteprima: AnteprimaImpattoOrdini;
  @Input() orgId: number;
  @Input() anagraficaId: number;

  // Ambito: l'operatore sceglie, non è una regola fissa di sistema (FR-010).
  soloInCorso = true;

  constructor(private modalController: ModalController) {}

  ngOnInit() {
    if (!this.diff) this.diff = { anagrafica: [], campiUtente: [], sedi: [] };
    if (!this.anteprima) this.anteprima = { disponibile: false, sedi: [] };
  }

  get conteggioDisponibile(): boolean {
    return this.anteprima.disponibile && !!this.anteprima.anagrafica;
  }

  get righeInCorso(): number { return this.anteprima.anagrafica?.righeInCorso ?? 0; }

  get righeTotali(): number { return this.anteprima.anagrafica?.righeTotali ?? 0; }

  // Conteggio dell'ambito scelto, in RIGHE d'ordine (FR-011): mostrare ordini e aggiornare righe
  // renderebbe il dialogo inaffidabile su ogni cliente con ordini a righe in stati misti.
  get righeSelezionate(): number {
    return this.soloInCorso ? this.righeInCorso : this.righeTotali;
  }

  // Sedi propagabili e sedi eliminate hanno resa diversa: le seconde portano solo un avviso.
  get sediPropagabili(): DiffSede[] { return this.diff.sedi.filter(s => !s.eliminata); }

  get sediEliminate(): DiffSede[] { return this.diff.sedi.filter(s => s.eliminata); }

  righeDiSedeEliminata(sedeId: number): number {
    return this.anteprima.sedi.find(s => s.sedeId === sedeId)?.righeTotali ?? 0;
  }

  campiUtentePropagabili(): ModificaCampo[] { return this.diff.campiUtente.filter(c => c.propagabile); }

  campiUtenteNonPropagabili() { return this.diff.campiUtente.filter(c => !c.propagabile); }

  // Non esiste una propagazione vuota: senza campi spuntati la conferma è disabilitata.
  // ⚠️ La disabilitazione dipende SOLO da questo, MAI dall'assenza del conteggio: legare le due
  // condizioni vanificherebbe il fail-open di FR-012 — l'operatore vedrebbe il dialogo e non
  // potrebbe confermare (FR-009, eccezione).
  get nessunCampoSpuntato(): boolean {
    return this.campiSelezionati().length === 0
      && this.campiUtenteSelezionati().length === 0
      && this.sediSelezionate().length === 0;
  }

  // Senza conteggio l'etichetta perde il numero, non l'abilitazione.
  get etichettaConferma(): string {
    return this.conteggioDisponibile
      ? `Aggiorna ${this.righeSelezionate} righe`
      : 'Aggiorna le righe collegate';
  }

  // Il gruppo indirizzo si spunta e si deseleziona come UN SOLO elemento: nella richiesta finiscono
  // tutti e tre i nomi o nessuno (FR-024).
  private campiSelezionati(): string[] {
    return this.diff.anagrafica.filter(m => m.selezionato).map(m => m.chiave);
  }

  private campiUtenteSelezionati(): string[] {
    return this.diff.campiUtente.filter(c => c.propagabile && c.selezionato).map(c => c.chiave);
  }

  private sediSelezionate(): { sedeId: number; campi: string[] }[] {
    return this.sediPropagabili
      .map(sede => ({ sedeId: sede.sedeId, campi: this.campiDiSede(sede) }))
      .filter(s => s.campi.length > 0);
  }

  private campiDiSede(sede: DiffSede): string[] {
    const singoli = sede.modifiche.filter(m => m.selezionato).map(m => m.chiave);
    const gruppo = sede.gruppoIndirizzo?.selezionato
      ? sede.gruppoIndirizzo.modifiche.map(m => m.chiave)
      : [];
    return [...gruppo, ...singoli];
  }

  conferma(): void {
    const request: PropagaAiOrdiniRequest = {
      orgId: this.orgId,
      anagraficaId: this.anagraficaId,
      soloInCorso: this.soloInCorso,
      campiAnagrafica: this.campiSelezionati(),
      campiUtente: this.campiUtenteSelezionati(),
      sedi: this.sediSelezionate(),
    };
    this.modalController.dismiss(request, 'confirm');
  }

  // "Non aggiornare": nessun ordine cambia e la modifica all'anagrafica resta salvata.
  annulla(): void {
    this.modalController.dismiss(null, 'cancel');
  }
}
